import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Building2, Plus, Trash2, Users, GraduationCap, LifeBuoy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function SchoolsAdmin() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", admin_email: "", admin_password: "", admin_name: "مدير المدرسة" });
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);

  const load = () =>
    api.get("/schools").then(({ data }) => setRows(data)).catch((e) => toast.error(formatApiError(e)));
  const loadTickets = () =>
    api.get("/support-tickets").then(({ data }) => setTickets(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); loadTickets(); }, []);

  const resolveTicket = async (t) => {
    try {
      await api.put(`/support-tickets/${t.id}/resolve`);
      toast.success("تم وضع علامة على المشكلة كمُعالَجة");
      loadTickets();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    if (!form.name.trim() || !form.admin_email || !form.admin_password) {
      toast.error("جميع الحقول مطلوبة");
      return;
    }
    setLoading(true);
    try {
      await api.post("/schools", form);
      toast.success(`تم إنشاء المدرسة «${form.name}»`);
      setOpen(false);
      setForm({ name: "", admin_email: "", admin_password: "", admin_name: "مدير المدرسة" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const del = async (row) => {
    if (!window.confirm(`حذف المدرسة «${row.name}» وجميع بياناتها؟ لا يمكن التراجع.`)) return;
    try {
      await api.delete(`/schools/${row.id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const columns = [
    { key: "name", header: "اسم المدرسة",
      render: (r) => <span className="font-semibold text-foreground">{r.name}</span> },
    { key: "admin_email", header: "بريد المدير",
      render: (r) => <span dir="ltr" className="text-muted-foreground text-xs">{r.admin_email || "—"}</span> },
    { key: "users_count", header: "المستخدمون", align: "end",
      render: (r) => (
        <span className="inline-flex items-center gap-1 tabular-nums">
          <Users className="h-3.5 w-3.5 text-muted-foreground" /> {r.users_count}
        </span>
      ) },
    { key: "students_count", header: "الطلاب", align: "end",
      render: (r) => (
        <span className="inline-flex items-center gap-1 tabular-nums">
          <GraduationCap className="h-3.5 w-3.5 text-muted-foreground" /> {r.students_count}
        </span>
      ) },
    { key: "created_at", header: "أُنشئت في",
      render: (r) => <span className="text-xs text-muted-foreground">
        {r.created_at ? new Date(r.created_at).toLocaleDateString("ar-EG") : "—"}
      </span> },
  ];

  return (
    <div className="space-y-6" data-testid="schools-page">
      <PageHeader
        icon={Building2}
        overline="لوحة المنصّة"
        title="إدارة المدارس"
        subtitle="كل مدرسة عزلتها كاملة عن الأخرى. أنشئ مدرسة → حدّد أول مدير لها."
        actions={
          <Button onClick={() => setOpen(true)} data-testid="schools-add-btn"
                  className="h-10 bg-white text-[#0E7469] hover:bg-[#F4D06F] hover:text-[#083F38] font-semibold">
            <Plus className="h-4 w-4 me-1.5" /> مدرسة جديدة
          </Button>
        }
      />

      <Tabs defaultValue="schools" className="space-y-4">
        <TabsList className="bg-white border border-border">
          <TabsTrigger value="schools" data-testid="tab-schools">
            <Building2 className="h-4 w-4 me-1.5" /> المدارس
          </TabsTrigger>
          <TabsTrigger value="tickets" data-testid="tab-tickets">
            <LifeBuoy className="h-4 w-4 me-1.5" /> الشكاوى الواردة
            {tickets.filter((t) => t.status === "open").length > 0 && (
              <Badge variant="destructive" className="ms-1.5 h-5 px-1.5">
                {tickets.filter((t) => t.status === "open").length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="schools">
          <DataTable
            columns={columns} rows={rows}
            searchKeys={["name", "admin_email"]}
            actions={(row) => (
              <Button size="icon" variant="ghost" className="text-destructive"
                       onClick={() => del(row)} data-testid={`school-del-${row.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            emptyText="لا توجد مدارس بعد."
            testId="schools-table"
          />
        </TabsContent>

        <TabsContent value="tickets">
          <div className="space-y-3">
            {tickets.length === 0 && (
              <div className="rounded-xl border border-dashed border-border bg-white p-12 text-center text-muted-foreground">
                لا توجد شكاوى واردة حتى الآن.
              </div>
            )}
            {tickets.map((t) => (
              <div key={t.id} className={`rounded-xl border bg-white p-4 ${t.status === "open" ? "border-amber-300" : "border-border opacity-70"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{t.subject}</span>
                      <Badge variant={t.status === "open" ? "destructive" : "secondary"}>
                        {t.status === "open" ? "مفتوحة" : "تمت المعالجة"}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {t.school_name} — {t.reporter_name} ({t.reporter_email}) —{" "}
                      {t.created_at ? new Date(t.created_at).toLocaleString("ar-EG") : ""}
                    </div>
                  </div>
                  {t.status === "open" && (
                    <Button size="sm" variant="outline" onClick={() => resolveTicket(t)} data-testid={`ticket-resolve-${t.id}`}>
                      <CheckCircle2 className="h-4 w-4 me-1.5" /> وضع كمُعالَجة
                    </Button>
                  )}
                </div>
                <p className="mt-3 text-sm whitespace-pre-wrap">{t.message}</p>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              <Building2 className="inline h-5 w-5 me-1.5" /> مدرسة جديدة
            </DialogTitle>
            <DialogDescription>
              أنشئ مدرسة معزولة بالكامل — بحساب أول مدير خاص بها.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 py-2">
            <div className="space-y-1.5">
              <Label>اسم المدرسة *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="school-form-name" />
            </div>
            <div className="space-y-1.5">
              <Label>اسم مدير المدرسة</Label>
              <Input value={form.admin_name}
                      onChange={(e) => setForm({ ...form, admin_name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>بريد المدير *</Label>
              <Input type="email" dir="ltr" value={form.admin_email}
                      onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                      data-testid="school-form-email" />
            </div>
            <div className="space-y-1.5">
              <Label>كلمة مرور المدير * (6 أحرف فأكثر)</Label>
              <Input type="password" dir="ltr" value={form.admin_password}
                      onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                      data-testid="school-form-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>إلغاء</Button>
            <Button onClick={save} disabled={loading} data-testid="school-form-save">
              {loading ? "جارٍ الإنشاء…" : "إنشاء المدرسة"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
